import { TranslateService } from '@ngx-translate/core';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import {
  BasePageMetaResolver,
  CmsService,
  PageRobotsMeta,
  Product,
  ProductPageMetaResolver,
  ProductScope,
  ProductService,
  RoutingService,
  TranslationService,
  WindowRef
} from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Request } from 'express';
import { isPlatformServer } from '@angular/common';
import { Bundle } from '../models';
import { BaseStoreService, ConfigurationService, CustomPageLinkService } from '../services';
import { SERVER_REQUEST } from '../constants';

interface StateCodes {
  productCode: string;
  bundleCode: string;
  params: string;
}

interface ProductAndBundle {
  product: Product;
  bundle: Bundle;
  pageType: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomProductPageMetaResolver extends ProductPageMetaResolver {
  private productAndBundle$: Observable<ProductAndBundle>;

  constructor(
    protected routingService: RoutingService,
    protected productService: ProductService,
    protected translation: TranslationService,
    protected basePageMetaResolver: BasePageMetaResolver,
    protected pageLinkService: CustomPageLinkService,
    private translateService: TranslateService,
    private baseStoreService: BaseStoreService,
    private cmsService: CmsService,
    private configurationService: ConfigurationService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(SERVER_REQUEST) private request: Request,
    @Optional() private windowRef: WindowRef
  ) {
    super(routingService, productService, translation, basePageMetaResolver, pageLinkService);
    this.productAndBundle$ = this.routingService.getRouterState().pipe(
      map(
        (state): StateCodes => ({
          params: state.state.params['param0'],
          productCode: state.state.params['productCode'],
          bundleCode: state.state.queryParams['bundle']
        })
      ),
      filter((stateCodes) => !!stateCodes && !!stateCodes.productCode),
      switchMap((stateCodes) =>
        this.productService.get(stateCodes.productCode, ProductScope.DETAILS).pipe(
          filter((p) => !!p),
          map(
            (product): ProductAndBundle => ({
              pageType: stateCodes.params,
              product,
              bundle: stateCodes.bundleCode
                ? product.bundles?.find((bundle) => bundle.id === stateCodes.bundleCode)
                : undefined
            })
          )
        )
      ),
      filter((productOrBundle) => !!productOrBundle && !!productOrBundle.product)
    );
  }

  resolveDescription(): Observable<string> {
    const page$ = this.cmsService.getCurrentPage().pipe(map((page) => page?.['descriptionTag']));
    const host = isPlatformServer(this.platformId)
      ? this.request.hostname
      : this.windowRef.nativeWindow.location.hostname;
    const hostUrl = `https://${host}`;
    return this.productAndBundle$.pipe(
      withLatestFrom(page$, this.baseStoreService.getBaseStore()),
      map(([productAndBundle, page, baseStore]) => {
        const bundleMetaDescription = productAndBundle?.bundle?.descriptionTag ?? productAndBundle?.bundle?.claim;
        const productMetaDescription = productAndBundle?.product?.descriptionTag ?? productAndBundle?.product?.claim;
        if (page) {
          return page.replace(/<siteBrand>/gi, baseStore['brandSite']);
        }
        return productAndBundle.bundle
          ? bundleMetaDescription
          : productAndBundle.pageType === 'faq'
            ? `${this.translateService.instant('SEO.faqDescription')} ${productAndBundle.product.name} ${
              baseStore['brandSite']
            } ${this.translateService.instant('SEO.online')} ${hostUrl}${productAndBundle.product.url}`
            : productAndBundle.pageType === 'instructions-for-use'
              ? `${this.translateService.instant('SEO.noticeDescriptionPDP')} ${productAndBundle.product.name} ${
                baseStore['brandSite']
              } ${productAndBundle.product.commercialCode} ${this.translateService.instant('SEO.online')} ${host}`
              : productMetaDescription;
      })
    );
    // return combineLatest([page$, this.baseStoreService.getBaseStore()]).pipe(
    //   filter(([page]) => !!page),
    //   map(([page, baseStore]) => page.replace(/<siteBrand>/gi, baseStore['brandSite']))
    // );
    /*
     const host = isPlatformServer(this.platformId)
      ? this.request.hostname
      : this.windowRef.nativeWindow.location.hostname;
    const hostUrl = `https://${host}`;
    return this.productAndBundle$.pipe(
      withLatestFrom(this.baseStoreService.getBaseStore()),
      map(([productAndBundle, baseStore]) =>
        !!productAndBundle.bundle
          ? productAndBundle.bundle.claim
          : productAndBundle.pageType === 'faq'
          ? `${this.translateService.instant('SEO.faqDescription')} ${productAndBundle.product.name} ${
              baseStore['brandSite']
            } ${this.translateService.instant('SEO.online')} ${hostUrl}${productAndBundle.product.url}`
          : productAndBundle.pageType === 'instructions-for-use'
          ? `${this.translateService.instant('SEO.noticeDescriptionPDP')} ${productAndBundle.product.name} ${
              baseStore['brandSite']
            } ${productAndBundle.product.commercialCode} ${this.translateService.instant('SEO.online')} ${host}`
          : productAndBundle.product.claim
      )
    );
    */
  }

  resolveImage(): Observable<string> {
    return this.productAndBundle$.pipe(
      map((productAndBundle) => {
        return productAndBundle.bundle
          ? (productAndBundle?.bundle?.galleryImages?.GALLERY as any)?.[0]?.main?.url
          : (productAndBundle?.product?.images?.PRIMARY as any)?.main?.url;
      })
    );
  }

  resolveTitle(): Observable<string> {
    const page$ = this.cmsService.getCurrentPage().pipe(map((page) => page?.['titleTag']));
    return this.productAndBundle$.pipe(
      withLatestFrom(this.baseStoreService.getBaseStore(), page$),
      switchMap(([productAndBundle, baseStore, page]) => {
        if (page) {
          if (productAndBundle.bundle) {
            if (productAndBundle.bundle.titleTag) {
              return of(productAndBundle.bundle.titleTag);
            } else {
              return this.configurationService.getConfig().pipe(
                map((config) => {
                  const pattern = config.seo?.pattern?.bundle?.titleTag;
                  const bundleName = productAndBundle.bundle.name;

                  return pattern?.replace(/{name}/gi, bundleName);
                })
              );
            }
          } else {
            return of(page.replace(/<siteBrand>/gi, (site) =>
              /[a-z]/.test(site)
                ? `${baseStore['brandSite']?.charAt(0).toUpperCase()}${baseStore['brandSite']?.toLowerCase().slice(1)}`
                : baseStore['brandSite']?.toUpperCase()
            ));
          }
        }
        return of(
          productAndBundle.bundle
            ? productAndBundle.bundle.name
            : productAndBundle.pageType === 'faq'
              ? `${this.translateService.instant('SEO.faqTitle')} ${productAndBundle.product.name} ${
                baseStore['brandSite']
              }`
              : productAndBundle.pageType === 'instructions-for-use'
                ? `${this.translateService.instant('SEO.noticeTitle')} ${productAndBundle.product.name} ${
                  productAndBundle.product.commercialCode
                }`
                : productAndBundle.product.name
        );
      })
    );
    // const page$ = this.cmsService.getCurrentPage().pipe(map((page) => page['titleTag']));
    // return combineLatest([page$, this.baseStoreService.getBaseStore()]).pipe(
    //   filter(([page]) => !!page),
    //   map(([page, baseStore]) => page.replace(/<siteBrand>/gi, baseStore['brandSite']))
    // );
  }

  resolveCanonicalUrl(): Observable<string> {
    return this.productAndBundle$.pipe(
      switchMap((productAndBundle) =>
        this.pageLinkService.getTranslatedCanonicalUrl({}, this.getCanonicalUrl(productAndBundle))
      )
    );
  }

  private getCanonicalUrl(productAndBundle: ProductAndBundle): string {
    if (productAndBundle.bundle?.canonicalURL) {
      return productAndBundle.bundle.canonicalURL;
    }

    const product = productAndBundle.product;
    if (!product?.isMarketing && !product?.onSale) {
      return product.urlCsp;
    }

    return product.url;
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.productAndBundle$.pipe(
      map((productAndBundle) => {
        return !productAndBundle.bundle && productAndBundle?.product?.['indexFollow'] === false
          ? [PageRobotsMeta.NOFOLLOW, PageRobotsMeta.NOINDEX]
          : [];
      })
    );
  }
}
